/**
 * 개요 페이지 상단의 현장 담당자 위젯 (테이블 형식).
 *
 * 이전: 개요 페이지 상단의 별도 ContactCard (단일 담당자 1명) → 카드 grid →
 *       현재: 테이블 (운영팀 요청 "테이블 형식으로"). 시스템 상태 KPI bar 는 제거.
 *
 * 운영자가 매일 보는 화면에서 가장 유용한 정보는 "지금 누구한테 연락하면 되나".
 * 추가는 관련 사이트 위젯과 같은 인라인 폼으로 이 자리에서 바로(admin/editor).
 * 수정·삭제는 현장 설정 페이지의 '담당자' 탭 — 설정 아이콘으로 진입.
 *
 * 컬럼: 이름 / 회사 / 직책 / 휴대폰 / 담당 내용. user_id 연결은 미도입.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  AddCircleOutline as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SiteContact, SiteContactService } from '../../../services/SiteContactService';
import usePermissions from '../../../hooks/usePermissions';

interface Props {
  projectId: number;
}

const ContactsBar: React.FC<Props> = ({ projectId }) => {
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const [contacts, setContacts] = useState<SiteContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 인라인 추가 폼 상태 (관련 사이트 위젯과 동일 패턴)
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', role: '', phone: '', responsibility: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    SiteContactService.list(projectId)
      .then((data) => {
        if (!cancelled) setContacts(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '담당자 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const goToSettings = () => navigate(`/project/${projectId}/settings?tab=contacts`);

  const setField = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const resetForm = () => {
    setForm({ name: '', company: '', role: '', phone: '', responsibility: '' });
    setAdding(false);
  };

  const handleAdd = async () => {
    const name = form.name.trim();
    if (!name) {
      setError('이름을 입력하세요.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await SiteContactService.create(projectId, {
        name,
        company: form.company.trim() || undefined,
        role: form.role.trim() || undefined,
        phone: form.phone.trim() || undefined,
        responsibility: form.responsibility.trim() || undefined,
      });
      setContacts((prev) => [...prev, created]);
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : '담당자 등록 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    // 외부 Paper 는 호출 측(OverviewSection)이 제공한다. 여기서는 내용만.
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: contacts.length === 0 && !adding ? 0 : 1.25, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={700}>
            현장 담당자
          </Typography>
          {!loading && (
            <Typography variant="caption" color="text.secondary">
              ({contacts.length}명)
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5}>
          {canEdit && !adding && (
            <Tooltip title="담당자 추가">
              <IconButton size="small" onClick={() => setAdding(true)} aria-label="담당자 추가">
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="현장 설정 → 담당자 탭에서 수정/삭제">
            <IconButton size="small" onClick={goToSettings} aria-label="담당자 설정">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* 인라인 추가 폼 — 페이지 이동 없이 바로 등록 (이름만 필수) */}
      {adding && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: contacts.length === 0 ? 0 : 1.5, flexWrap: 'wrap', rowGap: 1 }}
        >
          <TextField
            size="small"
            label="이름"
            required
            value={form.name}
            onChange={setField('name')}
            disabled={saving}
            sx={{ minWidth: 120 }}
          />
          <TextField
            size="small"
            label="회사"
            value={form.company}
            onChange={setField('company')}
            disabled={saving}
            sx={{ minWidth: 120 }}
          />
          <TextField
            size="small"
            label="직책"
            value={form.role}
            onChange={setField('role')}
            disabled={saving}
            sx={{ minWidth: 100 }}
          />
          <TextField
            size="small"
            label="휴대폰"
            placeholder="010-0000-0000"
            value={form.phone}
            onChange={setField('phone')}
            disabled={saving}
            sx={{ minWidth: 140 }}
          />
          <TextField
            size="small"
            label="담당 내용"
            value={form.responsibility}
            onChange={setField('responsibility')}
            disabled={saving}
            sx={{ flex: 1, minWidth: 160 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
          />
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              variant="contained"
              onClick={handleAdd}
              disabled={saving}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {saving ? '저장 중…' : '추가'}
            </Button>
            <IconButton size="small" onClick={resetForm} disabled={saving} aria-label="취소">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      )}

      {contacts.length === 0 && !loading && !adding ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ py: 1, color: 'text.secondary' }}
        >
          <Typography variant="body2">담당자가 등록되어 있지 않습니다.</Typography>
          {canEdit && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAdding(true)}
              sx={{ textTransform: 'none' }}
            >
              추가하기
            </Button>
          )}
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="현장 담당자 목록">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>이름</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>회사</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>직책</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>휴대폰</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>담당 내용</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((c) => (
                <ContactTableRow key={c.id} contact={c} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const ContactTableRow: React.FC<{ contact: SiteContact }> = ({ contact }) => {
  const telHref = contact.phone ? `tel:${contact.phone.replace(/\s+/g, '')}` : undefined;
  const dash = (
    <Typography component="span" variant="caption" color="text.disabled">
      —
    </Typography>
  );
  return (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {contact.name || dash}
      </TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>{contact.company || dash}</TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>{contact.role || dash}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {contact.phone ? (
          telHref ? (
            <Typography
              variant="body2"
              component="a"
              href={telHref}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {contact.phone}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {contact.phone}
            </Typography>
          )
        ) : (
          dash
        )}
      </TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>
        {contact.responsibility || dash}
      </TableCell>
    </TableRow>
  );
};

export default ContactsBar;
